import { VALIDATION_RULES } from "@monorepo/config";

/**
 * Advanced Password Strength Calculator (Entropy-Based)
 * 
 * Replaces simple heuristic with Information Theory entropy calculation.
 * Addresses NIST guidelines:
 * 1. Length is the primary factor.
 * 2. Complexity (character variety) increases the "pool size" but cannot compensate for lack of length.
 * 3. Checking for common patterns reduces the effective entropy.
 */

export interface PasswordStrengthResult {
  score: number; // 0-4
  feedback: string[];
  crackTime: string;
}

// ----------------------------------------------------------------------
// Constants & Patterns
// ----------------------------------------------------------------------

export const PASSWORD_PATTERNS = {
  LOWER: /[a-z]/,
  UPPER: /[A-Z]/,
  NUMBER: /[0-9]/,
  SPECIAL: /[^a-zA-Z0-9]/,
  REPEATED: /(.)\1{2,}/, // 3+ repeated characters
  // Common sequential patterns
  SEQUENTIAL: /(?:abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|123|234|345|456|567|678|789|890)/i,
  COMMON: [/^123456/, /password/i, /qwerty/i, /admin/i, /welcome/i, /login/i, /12345678/],
} as const;

// ----------------------------------------------------------------------
// Entropy Helper
// ----------------------------------------------------------------------

function calculateEntropy(password: string): number {
  if (!password) return 0;

  // 1. Determine Pool Size
  let poolSize = 0;
  if (PASSWORD_PATTERNS.LOWER.test(password)) poolSize += 26;
  if (PASSWORD_PATTERNS.UPPER.test(password)) poolSize += 26;
  if (PASSWORD_PATTERNS.NUMBER.test(password)) poolSize += 10;
  if (PASSWORD_PATTERNS.SPECIAL.test(password)) poolSize += 33; // Approx special chars

  if (poolSize === 0) return 0;

  // 2. Raw Entropy = Length * log2(PoolSize)
  let entropy = password.length * Math.log2(poolSize);

  // 3. Penalties (Bit Reduction)

  // Repeats: reduces effective length
  const matches = password.match(PASSWORD_PATTERNS.REPEATED);
  if (matches) {
    entropy -= matches.length * 2;
  }

  // Sequences: sequences are predictable
  if (PASSWORD_PATTERNS.SEQUENTIAL.test(password)) {
    entropy -= 10; // Significant penalty for sequences
  }

  // Common patterns
  if (PASSWORD_PATTERNS.COMMON.some(p => p.test(password))) {
    entropy -= 20;
  }

  return Math.max(0, entropy);
}

// ----------------------------------------------------------------------
// Main Logic
// ----------------------------------------------------------------------

/**
 * Analyze Password Strength using NIST-aligned Entropy Logic
 */
export function analyzePasswordStrength(password: string): PasswordStrengthResult {
  if (!password) {
    return {
      score: 0,
      feedback: ["Enter a password"],
      crackTime: "instant",
    };
  }

  const entropy = calculateEntropy(password);
  const feedback: string[] = [];

  // ----------------------------------------------------------------
  // SCORING THRESHOLDS (Bits)
  // ----------------------------------------------------------------
  // < 35: Very Weak
  // 35 - 59: Weak
  // 60 - 79: Fair
  // 80 - 99: Good (NIST recommended baseline for user passwords often ~80 bits)
  // > 100: Strong
  // ----------------------------------------------------------------

  let score = 0;
  if (entropy < 35) score = 0;
  else if (entropy < 60) score = 1;
  else if (entropy < 80) score = 2;
  else if (entropy < 100) score = 3;
  else score = 4;

  // ----------------------------------------------------------------
  // OVERRIDES & GATES
  // ----------------------------------------------------------------

  // GATE 1: Minimum Length
  // Regardless of complexity, if it's too short, it's not strong.
  if (password.length < VALIDATION_RULES.PASSWORD_MIN_LENGTH) {
    // Cap score at 1 ("Weak") even if entropy is high (e.g. short string of random complex chars)
    score = Math.min(score, 1);
    feedback.push(`Use at least ${VALIDATION_RULES.PASSWORD_MIN_LENGTH} characters`);
  }

  // GATE 2: Common Password
  const isCommon = PASSWORD_PATTERNS.COMMON.some(p => p.test(password));
  if (isCommon) {
    score = 0; // Immediate fail
    feedback.push("Avoid common words or patterns");
  }

  // GATE 3: Missing Mandatory Types (Alignment with Zod Schema)
  // We want to guide the user to meet the strict requirements (Lower, Upper, Number, Special)
  // even if the entropy is high.
  const missingTypes: string[] = [];
  if (!PASSWORD_PATTERNS.LOWER.test(password)) missingTypes.push("lowercase letter");
  if (!PASSWORD_PATTERNS.UPPER.test(password)) missingTypes.push("uppercase letter");
  if (!PASSWORD_PATTERNS.NUMBER.test(password)) missingTypes.push("number");
  if (!PASSWORD_PATTERNS.SPECIAL.test(password)) missingTypes.push("special character");

  if (missingTypes.length > 0) {
    // If multiple missing, generic message. If 1 or 2, specific.
    if (missingTypes.length === 1) {
      feedback.push(`Add a ${missingTypes[0]}`);
    } else if (missingTypes.length === 2) {
      feedback.push(`Add ${missingTypes.join(" and ")}`);
    } else {
      feedback.push("Add uppercase, number, and special characters");
    }

    // Cap score at 3 ("Good") if requirements are not met, preventing "Strong" (4)
    // unless it's a very long passphrase (e.g. > 20 chars) which implies high entropy
    // But since Zod enforces these strictly, we should probably cap it to avoid confusion.
    // Let's strictly cap at 3 so they don't think they are "done" (Green/Strong) when they can't submit.
    score = Math.min(score, 3);
  }

  // ----------------------------------------------------------------
  // FEEDBACK
  // ----------------------------------------------------------------

  if (feedback.length === 0) {
    if (score >= 4) feedback.push("Excellent password!");
    else if (score === 3) feedback.push("Great password");
    else if (score === 2) feedback.push("Good start, make it longer");
    else if (score <= 1) feedback.push("Too weak");
  }

  return {
    score,
    feedback,
    crackTime: estimateCrackTime(score),
  };
}

/**
 * Estimate crack time based on score
 */
function estimateCrackTime(score: number): string {
  const times = [
    "instant", // Score 0
    "minutes", // Score 1
    "hours",   // Score 2
    "days",    // Score 3
    "years",   // Score 4
  ];

  return times[score] || "instant";
}

/**
 * Check if password meets strict minimum requirements
 */
export function meetsMinimumRequirements(password: string): boolean {
  if (!password) return false;

  const isLengthValid = password.length >= VALIDATION_RULES.PASSWORD_MIN_LENGTH;
  const hasVariety =
    PASSWORD_PATTERNS.LOWER.test(password) &&
    PASSWORD_PATTERNS.UPPER.test(password) &&
    PASSWORD_PATTERNS.NUMBER.test(password) &&
    PASSWORD_PATTERNS.SPECIAL.test(password);

  return isLengthValid && hasVariety;
}
